// LegalFooter — Support + Privacy Statement, surfaced inside the product.
//
// PointClickCare technical-validation requirement: "Your integrated solution
// must include Support and a privacy statement." This renders a slim footer
// with both, opening in-app panels so the requirement is satisfied without
// depending on an external page. Swap the hrefs/emails for your live ones.

import React, { useState, useEffect } from "react";

// Imported, not re-declared: the same address appears in every error message
// the app shows, and two copies would eventually disagree.
import { SUPPORT_EMAIL, SUPPORT_PHONE } from "./supportContact";

const PRIVACY_EMAIL = SUPPORT_EMAIL;
const PRIVACY_UPDATED = "July 2026";

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,26,36,.55)", zIndex: 4000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--ed-canvas)", borderRadius: 14, maxWidth: 720, width: "100%", boxShadow: "0 24px 60px rgba(0,0,0,.3)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid #e7ecf0" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f2233" }}>{title}</div>
          <button onClick={onClose} aria-label="Close"
            style={{ border: "none", background: "#f1f5f8", borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 16, color: "#5f7280", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 200px)", overflowY: "auto", fontSize: 13.5, lineHeight: 1.65, color: "#33485a" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const H = ({ children }) => <div style={{ fontSize: 12.5, fontWeight: 800, color: "#0f2233", textTransform: "uppercase", letterSpacing: ".05em", margin: "16px 0 5px" }}>{children}</div>;

export default function LegalFooter() {
  const [open, setOpen] = useState(null); // "support" | "privacy" | null
  const linkBtn = { background: "none", border: "none", color: "#0e7490", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: 0 };

  return (
    <footer style={{ borderTop: "1px solid #e7ecf0", background: "#fafcfd", padding: "10px 20px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", fontSize: 12, color: "#8093a0" }}>
      <span>© {new Date().getFullYear()} SurvAIHealth LLC</span>
      <span aria-hidden="true">·</span>
      <button style={linkBtn} onClick={() => setOpen("support")}>Support</button>
      <span aria-hidden="true">·</span>
      <button style={linkBtn} onClick={() => setOpen("privacy")}>Privacy Statement</button>
      <span style={{ marginLeft: "auto", color: "#b3c0c9" }}>Integrates with PointClickCare® · read-only</span>

      {open === "support" && (
        <Modal title="Support" onClose={() => setOpen(null)}>
          <p>Need help with SurvAIHealth or the PointClickCare connection? We're here.</p>
          <H>Email</H>
          <p><a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#0e7490", fontWeight: 600 }}>{SUPPORT_EMAIL}</a> — response within one business day.</p>
          <H>Phone</H>
          <p><a href={`tel:+${SUPPORT_PHONE.replace(/[^0-9]/g, "")}`} style={{ color: "#0e7490", fontWeight: 600 }}>{SUPPORT_PHONE}</a></p>
          <H>What to include</H>
          <p>Your facility name, the screen or report involved, and (if relevant) the approximate time it happened. Do not include resident PHI in email.</p>
          <H>PointClickCare access</H>
          <p>Your organization controls SurvAIHealth's access to PointClickCare and can deactivate it at any time from PointClickCare under <em>My&nbsp;Apps&nbsp;›&nbsp;Integrations</em>.</p>
        </Modal>
      )}

      {open === "privacy" && (
        <Modal title="Privacy Statement" onClose={() => setOpen(null)}>
          <div style={{ fontSize: 12, color: "#8093a0", marginBottom: 4 }}>Last updated: {PRIVACY_UPDATED}</div>

          <H>Who we are</H>
          <p>SurvAIHealth LLC (“SurvAIHealth,” “we”) provides a survey-compliance and MDS-optimization service to skilled nursing facilities. This statement explains how we handle information when your facility connects SurvAIHealth to PointClickCare.</p>

          <H>What we access</H>
          <p>When your organization activates SurvAIHealth, we read a limited set of resident clinical and administrative data from PointClickCare — resident demographics, coverage, admission/transfer/discharge records, MDS assessments, medications, diagnoses/conditions, activities of daily living, vitals and observations, immunizations, admission/consent document metadata, and progress notes. We access this data on a <strong>read-only</strong> basis. SurvAIHealth does not write to, modify, or delete any PointClickCare record.</p>

          <H>Why we use it</H>
          <p>We use this information solely to compare chart evidence against the resident’s MDS and surface coding-accuracy and survey-readiness opportunities for your staff. Findings are presented only within SurvAIHealth to authorized users at your facility.</p>

          <H>Protected Health Information (PHI)</H>
          <p>The data we process may include PHI. We handle PHI under a Business Associate Agreement (BAA) with your facility, consistent with HIPAA. We use PHI only to provide the service; we do <strong>not</strong> sell it or use it for advertising.</p>

          <H>How we protect it</H>
          <p>All data is transmitted over HTTPS/TLS. Access and refresh tokens are encrypted at rest and are never written to logs. Access is scoped to the organizations and facilities that activate SurvAIHealth. Internal patient identifiers are used for analysis and are not displayed in any end-user screen keyed on those identifiers.</p>

          <H>Data sharing</H>
          <p>We do not sell your data. We share it only with subprocessors necessary to operate the service (secure cloud hosting and the AI model used to generate compliance analysis), under confidentiality and data-protection obligations, and as required by law.</p>

          <H>Retention</H>
          <p>We retain analysis results and derived findings for as long as your facility uses the service, and we delete or de-identify data on request or upon termination, subject to legal retention requirements.</p>

          <H>Your choices</H>
          <p>Your organization controls activation and can deactivate SurvAIHealth’s PointClickCare access at any time from PointClickCare (My&nbsp;Apps&nbsp;›&nbsp;Integrations), which stops all further data access.</p>

          <H>Contact</H>
          <p>Privacy requests: <a href={`mailto:${PRIVACY_EMAIL}`} style={{ color: "#0e7490", fontWeight: 600 }}>{PRIVACY_EMAIL}</a>. General support: <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "#0e7490", fontWeight: 600 }}>{SUPPORT_EMAIL}</a>.</p>
        </Modal>
      )}
    </footer>
  );
}
